import { z } from "zod";

/** Local date-time without offset: 2026-09-25T10:00 or with seconds. */
const localDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, "Expected a local date-time")
  .transform((v) => (v.length === 16 ? `${v}:00` : v));
const timeZoneSchema = z.string().min(1).max(64);

export const calendarSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  isDefault: z.boolean(),
});
export const calendarListSchema = z.object({ calendars: z.array(calendarSchema) });
export type CalendarList = z.infer<typeof calendarListSchema>;
export type Calendar = z.infer<typeof calendarSchema>;

/**
 * Field rules shared by create and patch. Defaults live only on the create schema: a patch must
 * leave out what the client did not send, or Zod would fill the default and erase stored data.
 */
const eventFields = {
  calendarId: z.string().min(1),
  title: z.string().trim().min(1).max(500),
  description: z.string().max(10_000).nullable(),
  location: z.string().trim().max(500).nullable(),
  start: localDateTimeSchema,
  timeZone: timeZoneSchema,
  durationMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 366),
  allDay: z.boolean(),
};

export const calendarEventInputSchema = z.object({
  ...eventFields,
  description: eventFields.description.default(null),
  location: eventFields.location.default(null),
  allDay: eventFields.allDay.default(false),
});
export type CalendarEventInput = z.infer<typeof calendarEventInputSchema>;

export const calendarEventPatchSchema = z.object(eventFields).partial();
export type CalendarEventPatch = z.infer<typeof calendarEventPatchSchema>;

/** Response shape: plain fields only, so it serialises without transforms or defaults. */
export const calendarEventSchema = z.object({
  id: z.string(),
  uid: z.string().nullable(),
  updatedAt: z.string().nullable(),
  calendarId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  start: z.string(),
  timeZone: z.string(),
  durationMinutes: z.number().int(),
  allDay: z.boolean(),
});
export type CalendarEvent = z.infer<typeof calendarEventSchema>;
export const calendarEventListSchema = z.object({ events: z.array(calendarEventSchema) });
export type CalendarEventList = z.infer<typeof calendarEventListSchema>;

export const eventRangeQuerySchema = z.object({
  from: localDateTimeSchema,
  to: localDateTimeSchema,
  timeZone: timeZoneSchema,
});
export type EventRangeQuery = z.infer<typeof eventRangeQuerySchema>;

export const addressBookSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
});
export const addressBookListSchema = z.object({ addressBooks: z.array(addressBookSchema) });
export type AddressBookList = z.infer<typeof addressBookListSchema>;
export type AddressBook = z.infer<typeof addressBookSchema>;

const contactFields = {
  addressBookId: z.string().min(1),
  givenName: z.string().trim().max(200),
  familyName: z.string().trim().max(200),
  emails: z.array(z.email().trim().toLowerCase()).max(10),
  phones: z.array(z.string().trim().min(1).max(50)).max(10),
  organization: z.string().trim().max(200).nullable(),
  notes: z.string().max(5_000).nullable(),
};

export const contactInputSchema = z.object({
  ...contactFields,
  givenName: contactFields.givenName.default(""),
  familyName: contactFields.familyName.default(""),
  emails: contactFields.emails.default([]),
  phones: contactFields.phones.default([]),
  organization: contactFields.organization.default(null),
  notes: contactFields.notes.default(null),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

export const contactPatchSchema = z.object(contactFields).partial();
export type ContactPatch = z.infer<typeof contactPatchSchema>;

export const contactSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  addressBookId: z.string(),
  givenName: z.string(),
  familyName: z.string(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  organization: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Contact = z.infer<typeof contactSchema>;
export const contactListSchema = z.object({ contacts: z.array(contactSchema) });
export type ContactList = z.infer<typeof contactListSchema>;

export const contactQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
});

// --- Tasks ------------------------------------------------------------------------------------

export const taskListSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
});
export type TaskList = z.infer<typeof taskListSchema>;
export const taskListsResponseSchema = z.object({ lists: z.array(taskListSchema) });
export type TaskListsResponse = z.infer<typeof taskListsResponseSchema>;
export const taskListCreateSchema = z.object({ name: z.string().trim().min(1).max(200) });

/** The message a task was created from; the web client links back to it. */
export const taskEmailSchema = z.object({
  id: z.string().min(1),
  subject: z.string().max(500),
});

const taskFields = {
  listId: z.string().min(1),
  title: z.string().trim().min(1).max(500),
  notes: z.string().max(10_000).nullable(),
  /** Local date-time; with `dueHasTime` false only the date part counts. */
  due: localDateTimeSchema.nullable(),
  dueHasTime: z.boolean(),
  timeZone: timeZoneSchema,
  important: z.boolean(),
  completed: z.boolean(),
  parentId: z.string().min(1).nullable(),
  email: taskEmailSchema.nullable(),
  /**
   * Minutes before the due moment; negative means after it (-540 is 09:00 on a date-only due
   * day). Needs a due date.
   */
  reminderMinutes: z
    .number()
    .int()
    .min(-1440)
    .max(60 * 24 * 30)
    .nullable(),
};

export const taskInputSchema = z.object({
  ...taskFields,
  notes: taskFields.notes.default(null),
  due: taskFields.due.default(null),
  dueHasTime: taskFields.dueHasTime.default(false),
  important: taskFields.important.default(false),
  completed: taskFields.completed.default(false),
  parentId: taskFields.parentId.default(null),
  email: taskFields.email.default(null),
  reminderMinutes: taskFields.reminderMinutes.default(null),
});
export type TaskInput = z.infer<typeof taskInputSchema>;

export const taskPatchSchema = z.object(taskFields).partial();
export type TaskPatch = z.infer<typeof taskPatchSchema>;

export const taskSchema = z.object({
  id: z.string(),
  uid: z.string().nullable(),
  updatedAt: z.string().nullable(),
  listId: z.string(),
  title: z.string(),
  notes: z.string().nullable(),
  due: z.string().nullable(),
  dueHasTime: z.boolean(),
  timeZone: z.string(),
  important: z.boolean(),
  completed: z.boolean(),
  parentId: z.string().nullable(),
  email: z.object({ id: z.string(), subject: z.string() }).nullable(),
  reminderMinutes: z.number().int().nullable(),
  /** Place in the user's own order; null for tasks made elsewhere and never ordered here. */
  position: z.number().nullable(),
});
export type Task = z.infer<typeof taskSchema>;

/** Every id of one level (top-level tasks of a list, or the subtasks of a task), in order. */
export const taskReorderSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(1_000),
});
export const tasksResponseSchema = z.object({ tasks: z.array(taskSchema) });
export type TasksResponse = z.infer<typeof tasksResponseSchema>;
export const taskQuerySchema = z.object({ listId: z.string().min(1).optional() });
